import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { auth } from '../services/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import AuthenticatedLayout from './AuthenticatedLayout'

/**
 * Auth gate: when not logged in and path !== '/', redirect to login.
 * When logged in, wrap children in AuthenticatedLayout (unified header + Outlet).
 */
export default function AuthAware() {
  const location = useLocation()
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null)
  const POST_AUTH_REDIRECT_KEY = 'utm:postAuthPath'

  useEffect(() => {
    if (!auth) {
      setIsLoggedIn(false)
      return
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user?.email)
    })
    return () => unsubscribe()
  }, [])

  // Still loading auth state
  if (isLoggedIn === null) {
    return null
  }

  // Not logged in: only allow '/' (login page)
  if (!isLoggedIn) {
    if (location.pathname === '/' || location.pathname === '') {
      return <Outlet />
    }
    const returnPath = `${location.pathname}${location.search}${location.hash}`
    if (returnPath && returnPath.startsWith('/') && !returnPath.startsWith('//')) {
      try {
        sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, returnPath)
      } catch {
        // ignore
      }
      return <Navigate to="/" replace />
    }
    return <Navigate to="/" replace />
  }

  // Logged in: preview / preview-embed 不包 layout（只顯示 template 內容，避免 UTM header/footer 進 iframe）
  if (location.pathname.match(/^\/(appfactory|appsuite)\/preview(?:-embed)?\//)) {
    return <Outlet />
  }

  // Logged in root keeps Home dashboard (it has its own full-page layout).
  if (location.pathname === '/' || location.pathname === '') {
    return <Outlet />
  }

  const skipAdminCheck = location.pathname.startsWith('/appsuite')

  // 其他頁面：統一 layout（header + content）
  return (
    <AuthenticatedLayout skipAdminCheck={skipAdminCheck}>
      <Outlet />
    </AuthenticatedLayout>
  )
}
